import { useTheme } from '@mui/material';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Typography from '@mui/material/Typography';
import type { TLessonBase, TSpaceBase } from '@onesource/schemas';
import type { TLessonWithBlocks } from '@onesource/services';
import { Link } from '@tanstack/react-router';
import DOMPurify from 'dompurify';

import { hasHTML } from '#/utils/has-html';

export interface LessonBreadcrumbsProps {
  spaceId: string;
  moduleId: string;
  lessonId: string;
  module?: TSpaceBase | null;
  lesson?: TLessonWithBlocks | null;
  parentLesson?: TLessonBase | null;
  currentLabel: string;
}

/**
 * Breadcrumbs for lesson content viewers (video / pdf / quiz).
 *
 * When `moduleId === spaceId` the space is a single-lesson space; we render a
 * single link back to `/community-hub/spaces/:id/lesson`. Otherwise we render the
 * full `Space > Module > (ParentLesson) > Lesson` chain used under container spaces.
 */
export function LessonBreadcrumbs({
  spaceId,
  moduleId,
  lessonId,
  module,
  lesson,
  parentLesson,
  currentLabel,
}: LessonBreadcrumbsProps) {
  const theme = useTheme();
  const linkStyle = { color: theme.palette.grey[600], textDecoration: 'none' };
  const isSingleLesson = moduleId === spaceId;

  return (
    <Breadcrumbs
      sx={{
        mb: { xs: 2, md: 3 },
        fontFamily: '"Figtree", sans-serif',
        fontSize: { xs: 14, md: 16 },
        lineHeight: { xs: '20px', md: '22px' },
        fontWeight: 400,
        color: theme.palette.grey[600],
        '& .MuiBreadcrumbs-separator': {
          mx: 0.6,
        },
      }}
    >
      {isSingleLesson && module && (
        <Link to="/community-hub/spaces/$id/lesson" params={{ id: spaceId }} style={linkStyle}>
          <Typography variant="body2" sx={{ mt: hasHTML(module.name) ? -0.5 : 0, fontSize: { xs: 14, md: 16 } }}>
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(module.name) }} />
          </Typography>
        </Link>
      )}
      {!isSingleLesson && module?.parent && (
        <Link to="/community-hub/spaces/$id" params={{ id: spaceId }} style={linkStyle}>
          <Typography variant="body2" sx={{ mt: hasHTML(module.parent.name) ? -0.5 : 0, fontSize: { xs: 14, md: 16 } }}>
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(module.parent.name) }} />
          </Typography>
        </Link>
      )}
      {!isSingleLesson && module && (
        <Link to="/community-hub/spaces/$id/modules/$moduleId" params={{ id: spaceId, moduleId }} style={linkStyle}>
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(module.name) }} />
        </Link>
      )}
      {!isSingleLesson && parentLesson && (
        <Link to="/community-hub/spaces/$id/modules/$moduleId" params={{ id: spaceId, moduleId }} style={linkStyle}>
          {parentLesson.title}
        </Link>
      )}
      {!isSingleLesson && lesson && (
        <Link
          to="/community-hub/spaces/$id/modules/$moduleId/lessons/$lessonId"
          params={{ id: spaceId, moduleId, lessonId }}
          style={linkStyle}
        >
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(lesson.title) }} />
        </Link>
      )}
      <Typography component="span" sx={{ fontSize: { xs: 14, md: 16 }, fontWeight: 700 }}>
        {currentLabel}
      </Typography>
    </Breadcrumbs>
  );
}
